/**
 * AnalyticsPanel — Night Sky Warehouse Analytics Dashboard
 * ═══════════════════════════════════════════════════════════
 * Visualizes BigQuery mart data: city rankings, regional overview,
 * dark sky calendar, weekly trends, and NEO risk summary.
 */
import { useState, useEffect } from "react";
import {
  fetchCityRankings,
  fetchRegionalOverview,
  fetchWeeklyTrends,
  fetchDarkSkyCalendar,
  fetchNeoRisk,
  fetchNEO,
} from "./api";

// ── Tiny sparkline bar (no recharts dependency) ──
const Bar = ({ value, max, color, width = 120 }) => (
  <div
    style={{
      width,
      height: 6,
      background: "rgba(255,255,255,0.06)",
      borderRadius: 3,
      overflow: "hidden",
    }}
  >
    <div
      style={{
        width: `${Math.min(100, (value / max) * 100)}%`,
        height: "100%",
        background: color,
        borderRadius: 3,
        transition: "width 0.6s ease",
      }}
    />
  </div>
);

// ── Score badge ──
const ScoreBadge = ({ score, size = 32 }) => {
  const color =
    score >= 80
      ? "#00ff88"
      : score >= 60
        ? "#44ddff"
        : score >= 40
          ? "#ffaa00"
          : "#ff4466";
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: `${color}18`,
        border: `1.5px solid ${color}55`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.38,
        fontWeight: 700,
        color,
        fontFamily: "'JetBrains Mono', monospace",
      }}
    >
      {Math.round(score)}
    </div>
  );
};

// ── Stat card ──
const StatCard = ({ label, value, sub, icon, color = "#44ddff" }) => (
  <div
    style={{
      background: "rgba(255,255,255,0.02)",
      border: "1px solid rgba(255,255,255,0.06)",
      borderRadius: 12,
      padding: "16px 20px",
      flex: 1,
      minWidth: 140,
    }}
  >
    <div
      style={{
        fontSize: 11,
        color: "#556677",
        textTransform: "uppercase",
        letterSpacing: 1.2,
        marginBottom: 6,
      }}
    >
      {icon && <span style={{ marginRight: 6 }}>{icon}</span>}
      {label}
    </div>
    <div
      style={{
        fontSize: 28,
        fontWeight: 700,
        color,
        fontFamily: "'JetBrains Mono', monospace",
      }}
    >
      {value}
    </div>
    {sub && (
      <div style={{ fontSize: 11, color: "#667788", marginTop: 4 }}>{sub}</div>
    )}
  </div>
);

// ── Night quality pill ──
const NightPill = ({ quality }) => {
  const map = {
    PRIME_NIGHT: { bg: "#00ff8820", color: "#00ff88", label: "Prime" },
    GOOD_NIGHT: { bg: "#44ddff20", color: "#44ddff", label: "Good" },
    AVERAGE_NIGHT: { bg: "#ffaa0020", color: "#ffaa00", label: "Average" },
    BRIGHT_NIGHT: { bg: "#ff446620", color: "#ff4466", label: "Bright" },
  };
  const s = map[quality] || map.AVERAGE_NIGHT;
  return (
    <span
      style={{
        padding: "2px 8px",
        borderRadius: 10,
        fontSize: 10,
        fontWeight: 600,
        background: s.bg,
        color: s.color,
        letterSpacing: 0.5,
      }}
    >
      {s.label}
    </span>
  );
};

// ── Section header ──
const SectionHeader = ({ icon, title, subtitle }) => (
  <div style={{ marginBottom: 16 }}>
    <h2
      style={{
        margin: 0,
        fontSize: 15,
        fontWeight: 600,
        color: "#ccddeeff",
        letterSpacing: 0.5,
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      <span style={{ fontSize: 18 }}>{icon}</span>
      {title}
    </h2>
    {subtitle && (
      <p style={{ margin: "4px 0 0", fontSize: 11, color: "#556677" }}>
        {subtitle}
      </p>
    )}
  </div>
);

// ── Tab button ──
const Tab = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    style={{
      padding: "8px 16px",
      background: active ? "rgba(68,221,255,0.1)" : "transparent",
      border: `1px solid ${active ? "rgba(68,221,255,0.3)" : "rgba(255,255,255,0.06)"}`,
      borderRadius: 8,
      color: active ? "#44ddff" : "#667788",
      cursor: "pointer",
      fontSize: 12,
      fontWeight: active ? 600 : 400,
      letterSpacing: 0.8,
      transition: "all 0.3s",
    }}
  >
    {children}
  </button>
);

// ═══════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════
export default function AnalyticsPanel({ onClose, lang = "en" }) {
  const [activeTab, setActiveTab] = useState("rankings");
  const [loading, setLoading] = useState(true);

  const t = {
    en: {
      warehouseAnalytics: "Warehouse Analytics",
      warehouseSub: "BIGQUERY · DBT · UPDATED HOURLY",
      rankings: "Rankings",
      regions: "Regions",
      calendar: "Calendar",
      neoRisk: "NEO Risk",
      queryingBQ: "Querying BigQuery warehouse…",
      topCities: "Top Stargazing Cities",
      topCitiesSub: "Ranked by composite visibility score from warehouse",
      globalAvg: "GLOBAL AVG",
      vsAvg: "vs avg",
      darkSky: "DARK SKY",
      rural: "RURAL",
      suburban: "SUBURBAN",
      urban: "URBAN",
      regionalOverview: "Regional Overview",
      regionalSub: "Continental stargazing conditions comparison",
      avgScore: "AVG SCORE",
      excellent: "EXCELLENT",
      good: "GOOD",
      darkSkyCities: "DARK SKY CITIES",
      weeklyTrends: "Weekly Trends",
      week: "Week",
      avg: "Avg",
      best: "Best",
      observations: "Obs",
      darkSkyCalendar: "Dark Sky Calendar",
      darkSkyCalendarSub: "Best upcoming nights for stargazing",
      moonPhase: "Moon Phase",
      illumination: "Illumination",
      quality: "Quality",
      recommended: "Recommended",
      prime: "PRIME",
      goodQ: "GOOD",
      average: "AVERAGE",
      bright: "BRIGHT",
      neoTracker: "Near-Earth Object Tracker",
      neoTrackerSub: "Live asteroid approach data from NASA NeoWs API",
      objectsTracked: "Objects Tracked",
      potentiallyHazardous: "Potentially Hazardous",
      nonHazardous: "Non-Hazardous",
      closestApproach: "Closest Approach",
      closestApproaches: "Closest Approaches",
      closestApproachesSub: "Sorted by miss distance — closest to Earth first",
      name: "Name",
      diameter: "Diameter",
      velocity: "Velocity",
      missDist: "Miss Dist",
      lunarDistances: "Lunar Distances",
      sizeComparison: "Asteroid Size Comparison",
      sizeComparisonSub: "Largest tracked objects vs familiar landmarks",
      person: "Person",
      bus: "Bus",
      statueOfLiberty: "Statue of Liberty",
      eiffelTower: "Eiffel Tower",
      fastestAsteroids: "Fastest Asteroids",
      fastestSub: "Top 10 by relative velocity",
      speedOfLight: "speed of light",
      warehouseSummary: "Warehouse Summary",
      warehouseSummarySub: "Aggregated from BigQuery dbt mart",
      hazardous: "Potentially Hazardous",
      avgDiameter: "Avg Diameter",
      maxDiameter: "Max Diameter",
      avgVelocity: "Avg Velocity",
      minMissDist: "Min Miss Dist",
      dataSource: "Data Source:",
      dataSourceText:
        "Live data from NASA NeoWs API via Supabase. Warehouse summary from BigQuery → dbt staging → dbt mart. Refreshed hourly via Cloud Scheduler → Cloud Run Job.",
    },
    ar: {
      warehouseAnalytics: "التحليلات",
      warehouseSub: "BigQuery · dbt · تحديث كل ساعة",
      rankings: "الترتيب",
      regions: "المناطق",
      calendar: "التقويم",
      neoRisk: "كويكبات",
      queryingBQ: "جاري تحميل البيانات...",
      topCities: "أفضل مدن رصد النجوم",
      topCitiesSub: "مرتبة حسب درجة الرصد",
      globalAvg: "المعدل العالمي",
      vsAvg: "عن المعدل",
      darkSky: "سماء مظلمة",
      rural: "ريفي",
      suburban: "ضواحي",
      urban: "حضري",
      regionalOverview: "نظرة عامة",
      regionalSub: "مقارنة ظروف الرصد حسب القارة",
      avgScore: "متوسط التقييم",
      excellent: "ممتاز",
      good: "جيد",
      darkSkyCities: "مدن السماء المظلمة",
      weeklyTrends: "اتجاهات أسبوعية",
      week: "أسبوع",
      avg: "متوسط",
      best: "الأفضل",
      observations: "أرصاد",
      darkSkyCalendar: "تقويم الرصد",
      darkSkyCalendarSub: "أفضل الليالي القادمة لرصد النجوم",
      moonPhase: "طور القمر",
      illumination: "الإضاءة",
      quality: "الجودة",
      recommended: "موصى بها",
      prime: "مثالي",
      goodQ: "جيد",
      average: "متوسط",
      bright: "مضيء",
      neoTracker: "متتبع الكويكبات",
      neoTrackerSub: "بيانات مباشرة من NASA",
      objectsTracked: "أجرام مرصودة",
      potentiallyHazardous: "خطرة محتملاً",
      nonHazardous: "غير خطرة",
      closestApproach: "أقرب اقتراب",
      closestApproaches: "أقرب الاقترابات",
      closestApproachesSub: "مرتبة حسب المسافة — الأقرب أولاً",
      name: "الاسم",
      diameter: "القطر",
      velocity: "السرعة",
      missDist: "المسافة",
      lunarDistances: "بُعد قمري",
      sizeComparison: "مقارنة الأحجام",
      sizeComparisonSub: "أكبر الكويكبات مقارنة بمعالم معروفة",
      person: "إنسان",
      bus: "حافلة",
      statueOfLiberty: "تمثال الحرية",
      eiffelTower: "برج إيفل",
      fastestAsteroids: "الأسرع",
      fastestSub: "أسرع 10 كويكبات",
      speedOfLight: "سرعة الضوء",
      warehouseSummary: "ملخص البيانات",
      warehouseSummarySub: "من BigQuery + dbt",
      hazardous: "خطرة محتملاً",
      avgDiameter: "متوسط القطر",
      maxDiameter: "أكبر قطر",
      avgVelocity: "متوسط السرعة",
      minMissDist: "أقل مسافة",
      dataSource: "المصدر:",
      dataSourceText:
        "بيانات مباشرة من NASA عبر Supabase. تحليلات من BigQuery + dbt. تحديث كل ساعة.",
    },
  }[lang];
  const [data, setData] = useState({
    rankings: null,
    regional: null,
    trends: null,
    calendar: null,
    neoRisk: null,
    neoLive: null,
  });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const [rankings, regional, trends, calendar, neoRisk, neoLive] =
        await Promise.all([
          fetchCityRankings(50),
          fetchRegionalOverview(),
          fetchWeeklyTrends(30),
          fetchDarkSkyCalendar(60),
          fetchNeoRisk(),
          fetchNEO().catch(() => []),
        ]);
      if (!cancelled) {
        setData({ rankings, regional, trends, calendar, neoRisk, neoLive });
        setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "#080c14",
        color: "#ccddee",
        fontFamily:
          lang === "ar"
            ? "'Noto Sans Arabic', 'Inter', sans-serif"
            : "'Inter', 'Segoe UI', sans-serif",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        direction: "ltr",
      }}
    >
      {/* ── Ambient glow ── */}
      <div
        style={{
          position: "absolute",
          top: -200,
          left: "30%",
          width: 600,
          height: 600,
          background:
            "radial-gradient(circle, rgba(68,221,255,0.04) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: -300,
          right: "20%",
          width: 800,
          height: 800,
          background:
            "radial-gradient(circle, rgba(0,255,136,0.03) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      {/* ── HEADER ── */}
      <div
        style={{
          padding: "20px 32px",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: 20,
              fontWeight: 700,
              color: "#eef4ff",
              letterSpacing: -0.3,
            }}
          >
            <span style={{ color: "#44ddff", marginRight: 10 }}>✦</span>
            {t.warehouseAnalytics}
          </h1>
          <p
            style={{
              margin: "4px 0 0",
              fontSize: 11,
              color: "#445566",
              letterSpacing: 1.2,
              textTransform: "uppercase",
            }}
          >
            {t.warehouseSub}
          </p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ display: "flex", gap: 6 }}>
            <Tab
              active={activeTab === "rankings"}
              onClick={() => setActiveTab("rankings")}
            >
              🏆 {t.rankings}
            </Tab>
            <Tab
              active={activeTab === "regions"}
              onClick={() => setActiveTab("regions")}
            >
              🌍 {t.regions}
            </Tab>
            <Tab
              active={activeTab === "calendar"}
              onClick={() => setActiveTab("calendar")}
            >
              🌙 {t.calendar}
            </Tab>
            <Tab
              active={activeTab === "neo"}
              onClick={() => setActiveTab("neo")}
            >
              ☄️ {t.neoRisk}
            </Tab>
          </div>

          <button
            onClick={onClose}
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              color: "#8899aa",
              cursor: "pointer",
              fontSize: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.3s",
              marginLeft: 8,
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "rgba(255,68,102,0.15)";
              e.target.style.color = "#ff4466";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "rgba(255,255,255,0.04)";
              e.target.style.color = "#8899aa";
            }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* ── CONTENT ── */}
      <div
        style={{
          flex: 1,
          overflow: "auto",
          padding: "24px 32px 40px",
        }}
      >
        {loading ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "50vh",
              flexDirection: "column",
              gap: 16,
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                border: "2px solid rgba(68,221,255,0.2)",
                borderTop: "2px solid #44ddff",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
            <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
            <p style={{ color: "#556677", fontSize: 13 }}>{t.queryingBQ}</p>
          </div>
        ) : (
          <>
            {activeTab === "rankings" && <RankingsTab data={data} t={t} />}
            {activeTab === "regions" && <RegionsTab data={data} t={t} />}
            {activeTab === "calendar" && <CalendarTab data={data} t={t} />}
            {activeTab === "neo" && <NeoTab data={data} t={t} />}
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  RANKINGS TAB
// ═══════════════════════════════════════════════════════════
function RankingsTab({ data, t }) {
  const rankings = data.rankings?.rankings || [];
  const top = rankings.slice(0, 25);
  const maxScore = Math.max(...top.map((r) => r.avg_score || 0), 1);

  return (
    <div>
      <SectionHeader icon="🏆" title={t.topCities} subtitle={t.topCitiesSub} />

      {/* Summary stats */}
      <div
        style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}
      >
        <StatCard
          icon="🥇"
          label="Best City"
          value={top[0]?.city || "—"}
          sub={`${top[0]?.avg_score || 0} avg score · ${top[0]?.continent || ""}`}
          color="#00ff88"
        />
        <StatCard
          icon="📊"
          label="Avg Score"
          value={
            rankings.length
              ? Math.round(
                  rankings.reduce((s, r) => s + (r.avg_score || 0), 0) /
                    rankings.length,
                )
              : "—"
          }
          sub={`Across ${rankings.length} cities`}
          color="#44ddff"
        />
        <StatCard
          icon="⭐"
          label="Excellent Nights"
          value={
            top[0]?.pct_excellent_nights != null
              ? `${top[0].pct_excellent_nights}%`
              : "—"
          }
          sub={`${top[0]?.city || ""} · score ≥ 80`}
          color="#ffaa00"
        />
      </div>

      {/* Rankings table */}
      <div
        style={{
          background: "rgba(255,255,255,0.015)",
          border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: 12,
          overflow: "hidden",
        }}
      >
        {/* Table header */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "36px minmax(100px, 200px) 56px 90px 24px 1fr 65px",
            padding: "10px 16px",
            fontSize: 10,
            color: "#445566",
            textTransform: "uppercase",
            letterSpacing: 1.2,
            borderBottom: "1px solid rgba(255,255,255,0.04)",
          }}
        >
          <span>#</span>
          <span>City</span>
          <span>Score</span>
          <span>Tier</span>
          <span />
          <span style={{ textAlign: "center" }}>Bar</span>
          <span style={{ textAlign: "right" }}>vs Avg</span>
        </div>

        {/* Rows */}
        {top.map((r, i) => (
          <div
            key={r.city}
            style={{
              display: "grid",
              gridTemplateColumns:
                "36px minmax(100px, 200px) 56px 90px 24px 1fr 65px",
              padding: "10px 16px",
              alignItems: "center",
              borderBottom: "1px solid rgba(255,255,255,0.025)",
              background:
                i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)",
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(68,221,255,0.04)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background =
                i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)")
            }
          >
            <span
              style={{
                color: i < 3 ? ["#ffd700", "#c0c0c0", "#cd7f32"][i] : "#556677",
                fontWeight: i < 3 ? 700 : 400,
                fontSize: 13,
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              {r.global_rank}
            </span>

            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: "#ddeeff" }}>
                {r.city}
              </div>
              <div style={{ fontSize: 10, color: "#556677" }}>
                {r.country} · {r.continent}
              </div>
            </div>

            <ScoreBadge score={r.avg_score} size={30} />

            <span
              style={{
                padding: "2px 8px",
                borderRadius: 10,
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: 0.5,
                background:
                  r.light_pollution_tier === "DARK_SKY"
                    ? "rgba(0,255,136,0.12)"
                    : r.light_pollution_tier === "RURAL"
                      ? "rgba(68,221,255,0.12)"
                      : "rgba(255,170,0,0.12)",
                color:
                  r.light_pollution_tier === "DARK_SKY"
                    ? "#00ff88"
                    : r.light_pollution_tier === "RURAL"
                      ? "#44ddff"
                      : "#ffaa00",
              }}
            >
              {(r.light_pollution_tier || "").replace("_", " ")}
            </span>

            <span />
            <Bar
              value={r.avg_score}
              max={maxScore}
              color="#44ddff"
              width="100%"
            />

            <span
              style={{
                textAlign: "right",
                fontSize: 12,
                fontFamily: "'JetBrains Mono', monospace",
                color: (r.vs_global_avg || 0) >= 0 ? "#00ff88" : "#ff4466",
              }}
            >
              {(r.vs_global_avg || 0) >= 0 ? "+" : ""}
              {r.vs_global_avg?.toFixed(1) || "0.0"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  REGIONS TAB
// ═══════════════════════════════════════════════════════════
function RegionsTab({ data, t }) {
  const regions = data.regional?.regions || [];
  const trends = data.trends?.trends || [];
  const maxAvg = Math.max(...regions.map((r) => r.avg_score || 0), 1);

  return (
    <div>
      <SectionHeader
        icon="🌍"
        title={t.regionalOverview}
        subtitle={t.regionalSub}
      />

      {/* Region cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 12,
          marginBottom: 32,
        }}
      >
        {regions.map((r) => (
          <div
            key={r.region}
            style={{
              background: "rgba(255,255,255,0.02)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 12,
              padding: 16,
              transition: "border-color 0.3s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.borderColor = "rgba(68,221,255,0.2)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)")
            }
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: 12,
              }}
            >
              <div>
                <div
                  style={{ fontSize: 14, fontWeight: 600, color: "#ddeeff" }}
                >
                  {r.region}
                </div>
                <div style={{ fontSize: 11, color: "#556677" }}>
                  {r.continent}
                </div>
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "#44ddff",
                  background: "rgba(68,221,255,0.1)",
                  padding: "2px 8px",
                  borderRadius: 10,
                  fontWeight: 600,
                }}
              >
                #{r.region_rank}
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 10,
              }}
            >
              <ScoreBadge score={r.avg_score} size={38} />
              <div>
                <div style={{ fontSize: 11, color: "#889900" }}>
                  <span style={{ color: "#00ff88" }}>{r.pct_excellent}%</span>{" "}
                  excellent ·{" "}
                  <span style={{ color: "#44ddff" }}>{r.pct_good_plus}%</span>{" "}
                  good+
                </div>
                <div style={{ fontSize: 10, color: "#445566", marginTop: 2 }}>
                  {r.cities_monitored} cities · {r.dark_sky_cities} dark sky
                </div>
              </div>
            </div>

            <Bar
              value={r.avg_score}
              max={maxAvg}
              color="#44ddff"
              width="100%"
            />

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 8,
                fontSize: 10,
                color: "#556677",
              }}
            >
              <span>Best: {r.best_score}</span>
              <span>Weather: {r.avg_weather}</span>
              <span>LP: {r.avg_light_pollution}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Weekly trends */}
      {trends.length > 0 && (
        <>
          <SectionHeader
            icon="📈"
            title="Weekly Trends"
            subtitle="Top improving and declining cities this week"
          />
          <div
            style={{
              background: "rgba(255,255,255,0.015)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 12,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 90px 90px 130px",
                padding: "10px 16px",
                fontSize: 10,
                color: "#445566",
                textTransform: "uppercase",
                letterSpacing: 1.2,
                borderBottom: "1px solid rgba(255,255,255,0.04)",
              }}
            >
              <span>City</span>
              <span>Avg Score</span>
              <span>Best</span>
              <span style={{ textAlign: "center" }}>Bar</span>
            </div>
            {trends.slice(0, 15).map((t, i) => (
              <div
                key={t.city}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 90px 90px 130px",
                  padding: "8px 16px",
                  alignItems: "center",
                  borderBottom: "1px solid rgba(255,255,255,0.025)",
                  fontSize: 12,
                }}
              >
                <div>
                  <span style={{ color: "#ddeeff" }}>{t.city}</span>
                  <span
                    style={{ color: "#445566", marginLeft: 8, fontSize: 10 }}
                  >
                    {t.continent}
                  </span>
                </div>
                <ScoreBadge score={t.avg_score} size={26} />
                <span
                  style={{
                    color: "#667788",
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 11,
                  }}
                >
                  {t.best_score}
                </span>
                <Bar
                  value={t.avg_score}
                  max={100}
                  color="#44ddff"
                  width={120}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  CALENDAR TAB
// ═══════════════════════════════════════════════════════════
function CalendarTab({ data, t }) {
  const calendar = data.calendar?.calendar || [];

  // Group by date
  const byDate = {};
  calendar.forEach((c) => {
    const d = c.phase_date?.split("T")[0] || c.phase_date;
    if (!byDate[d]) byDate[d] = { ...c, cities: [] };
    byDate[d].cities.push(c);
  });

  const dates = Object.keys(byDate).sort().slice(0, 15);

  return (
    <div>
      <SectionHeader
        icon="🌙"
        title={t.darkSkyCalendar}
        subtitle={t.darkSkyCalendarSub}
      />

      {/* Moon legend */}
      <div
        style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}
      >
        {[
          { q: "PRIME_NIGHT", l: "Prime (< 10% moon)" },
          { q: "GOOD_NIGHT", l: "Good (< 30%)" },
          { q: "AVERAGE_NIGHT", l: "Average (< 60%)" },
          { q: "BRIGHT_NIGHT", l: "Bright (60%+)" },
        ].map(({ q, l }) => (
          <div
            key={q}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 11,
              color: "#667788",
            }}
          >
            <NightPill quality={q} /> {l}
          </div>
        ))}
      </div>

      {/* Calendar entries */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {dates.map((date) => {
          const entry = byDate[date];
          const cities = entry.cities.sort(
            (a, b) =>
              (b.recommendation_score || 0) - (a.recommendation_score || 0),
          );
          const topCity = cities[0];

          return (
            <div
              key={date}
              style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: 12,
                padding: 16,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 10,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 10,
                      background: "rgba(255,255,255,0.04)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: "#ddeeff",
                        lineHeight: 1,
                      }}
                    >
                      {new Date(date + "T12:00:00").getDate()}
                    </div>
                    <div
                      style={{
                        fontSize: 9,
                        color: "#556677",
                        textTransform: "uppercase",
                      }}
                    >
                      {new Date(date + "T12:00:00").toLocaleDateString("en", {
                        month: "short",
                      })}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: "#ddeeff",
                      }}
                    >
                      {topCity?.phase_name || "—"}
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: 11,
                          color: "#556677",
                        }}
                      >
                        {topCity?.illumination_pct != null
                          ? `${Math.round(topCity.illumination_pct)}% lit`
                          : ""}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: "#556677" }}>
                      {topCity?.moon_impact || ""}
                    </div>
                  </div>
                </div>
                <NightPill quality={topCity?.night_quality} />
              </div>

              {/* Recommended cities */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {cities.slice(0, 5).map((c) => (
                  <div
                    key={c.recommended_city}
                    style={{
                      padding: "6px 12px",
                      background: "rgba(255,255,255,0.03)",
                      border: "1px solid rgba(255,255,255,0.05)",
                      borderRadius: 8,
                      fontSize: 11,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span style={{ color: "#ddeeff", fontWeight: 500 }}>
                      {c.recommended_city}
                    </span>
                    <span style={{ color: "#445566", fontSize: 10 }}>
                      {c.continent}
                    </span>
                    <span
                      style={{
                        color: "#44ddff",
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    >
                      {Math.round(c.recommendation_score || 0)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  NEO TAB
// ═══════════════════════════════════════════════════════════
function NeoTab({ data, t }) {
  const risk = data.neoRisk?.risk_summary || [];
  const neos = Array.isArray(data.neoLive) ? data.neoLive : [];

  // Derived stats
  const hazardous = neos.filter((n) => n.is_potentially_hazardous);
  const nonHazardous = neos.filter((n) => !n.is_potentially_hazardous);
  const sorted = [...neos].sort(
    (a, b) =>
      parseFloat(a.miss_distance_km || 1e15) -
      parseFloat(b.miss_distance_km || 1e15),
  );
  const closest = sorted.slice(0, 15);
  const maxMiss = Math.max(
    ...closest.map((n) => parseFloat(n.miss_distance_km || 0)),
    1,
  );
  const lunarDist = 384400; // km

  // Biggest asteroids
  const biggest = [...neos]
    .sort(
      (a, b) =>
        parseFloat(b.estimated_diameter_max_m || 0) -
        parseFloat(a.estimated_diameter_max_m || 0),
    )
    .slice(0, 10);
  const maxDiam = Math.max(
    ...biggest.map((n) => parseFloat(n.estimated_diameter_max_m || 0)),
    1,
  );

  // Size references (meters)
  const sizeRefs = [
    { label: t.person, size: 1.8, color: "#44ddff" },
    { label: t.bus, size: 12, color: "#88aacc" },
    { label: t.statueOfLiberty, size: 93, color: "#aabbcc" },
    { label: t.eiffelTower, size: 330, color: "#667788" },
  ];

  const fmtDist = (km) => {
    const v = parseFloat(km || 0);
    if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M km`;
    if (v >= 1e3) return `${(v / 1e3).toFixed(0)}K km`;
    return `${v.toFixed(0)} km`;
  };

  const fmtLunar = (km) => `${(parseFloat(km || 0) / lunarDist).toFixed(2)} LD`;

  return (
    <div>
      <SectionHeader
        icon="☄️"
        title={t.neoTracker}
        subtitle={t.neoTrackerSub}
      />

      {/* ── HERO STATS ── */}
      <div
        style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 28 }}
      >
        {[
          {
            label: t.objectsTracked,
            value: neos.length,
            color: "#44ddff",
            icon: "🔭",
          },
          {
            label: t.potentiallyHazardous,
            value: hazardous.length,
            color: "#ff4466",
            icon: "⚠️",
          },
          {
            label: t.nonHazardous,
            value: nonHazardous.length,
            color: "#00ff88",
            icon: "✓",
          },
          {
            label: t.closestApproach,
            value: sorted[0] ? fmtDist(sorted[0].miss_distance_km) : "—",
            color: "#ffaa44",
            icon: "📏",
          },
        ].map(({ label, value, color, icon }) => (
          <div
            key={label}
            style={{
              flex: "1 1 140px",
              background: `linear-gradient(135deg, ${color}08, ${color}03)`,
              border: `1px solid ${color}25`,
              borderRadius: 12,
              padding: "16px 18px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 20, marginBottom: 4 }}>{icon}</div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                color,
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              {value}
            </div>
            <div
              style={{
                fontSize: 9,
                color: "#667788",
                letterSpacing: 1,
                textTransform: "uppercase",
                marginTop: 4,
              }}
            >
              {label}
            </div>
          </div>
        ))}
      </div>

      {/* ── CLOSEST APPROACHES TABLE ── */}
      <SectionHeader
        icon="📏"
        title={t.closestApproaches}
        subtitle={t.closestApproachesSub}
      />
      <div
        style={{
          background: "rgba(255,255,255,0.015)",
          border: "1px solid rgba(255,255,255,0.05)",
          borderRadius: 12,
          overflow: "hidden",
          marginBottom: 28,
        }}
      >
        {/* Table header */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr 1fr 1fr 3fr",
            gap: 8,
            padding: "10px 16px",
            borderBottom: "1px solid rgba(255,255,255,0.05)",
            fontSize: 9,
            color: "#556677",
            letterSpacing: 1,
            textTransform: "uppercase",
          }}
        >
          <span>{t.name}</span>
          <span>{t.diameter}</span>
          <span>{t.velocity}</span>
          <span>{t.missDist}</span>
          <span>{t.lunarDistances}</span>
        </div>

        {closest.map((neo, i) => {
          const miss = parseFloat(neo.miss_distance_km || 0);
          const ld = miss / lunarDist;
          const isHz = neo.is_potentially_hazardous;
          const barW = Math.min(100, (miss / maxMiss) * 100);
          // Color based on lunar distance
          const barColor = ld < 5 ? "#ff4466" : ld < 20 ? "#ffaa44" : "#00ff88";

          return (
            <div
              key={neo.neo_id || neo.name || i}
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 1fr 1fr 1fr 3fr",
                gap: 8,
                padding: "10px 16px",
                borderBottom: "1px solid rgba(255,255,255,0.02)",
                alignItems: "center",
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "rgba(68,221,255,0.03)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
            >
              <div>
                <span
                  style={{ fontSize: 12, color: "#c0d0e0", fontWeight: 500 }}
                >
                  {isHz && (
                    <span
                      style={{ color: "#ff4466", marginRight: 4, fontSize: 10 }}
                    >
                      ⚠
                    </span>
                  )}
                  {(neo.name || "Unknown")
                    .replace(/[()]/g, "")
                    .substring(0, 22)}
                </span>
                {neo.close_approach_date && (
                  <div style={{ fontSize: 9, color: "#445566", marginTop: 2 }}>
                    {neo.close_approach_date}
                  </div>
                )}
              </div>
              <span
                style={{
                  fontSize: 11,
                  color: "#aabbcc",
                  fontFamily: "'JetBrains Mono', monospace",
                }}
              >
                {parseFloat(neo.estimated_diameter_max_m || 0).toFixed(0)}m
              </span>
              <span
                style={{
                  fontSize: 11,
                  color: "#aabbcc",
                  fontFamily: "'JetBrains Mono', monospace",
                }}
              >
                {parseFloat(neo.relative_velocity_kms || 0).toFixed(1)}
                <span style={{ fontSize: 8, color: "#556677" }}> km/s</span>
              </span>
              <span
                style={{
                  fontSize: 11,
                  color: barColor,
                  fontFamily: "'JetBrains Mono', monospace",
                }}
              >
                {fmtLunar(neo.miss_distance_km)}
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div
                  style={{
                    flex: 1,
                    height: 6,
                    background: "rgba(255,255,255,0.04)",
                    borderRadius: 3,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${barW}%`,
                      height: "100%",
                      background: `linear-gradient(90deg, ${barColor}, ${barColor}88)`,
                      borderRadius: 3,
                      transition: "width 0.6s ease",
                    }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── SIZE COMPARISON ── */}
      <SectionHeader
        icon="📐"
        title={t.sizeComparison}
        subtitle={t.sizeComparisonSub}
      />
      <div
        style={{
          background: "rgba(255,255,255,0.015)",
          border: "1px solid rgba(255,255,255,0.05)",
          borderRadius: 12,
          padding: 20,
          marginBottom: 28,
        }}
      >
        {/* Reference objects */}
        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            marginBottom: 16,
          }}
        >
          {sizeRefs.map((ref) => (
            <span
              key={ref.label}
              style={{
                fontSize: 9,
                color: ref.color,
                background: `${ref.color}10`,
                border: `1px solid ${ref.color}30`,
                borderRadius: 4,
                padding: "3px 8px",
                letterSpacing: 0.5,
              }}
            >
              {ref.label}: {ref.size}m
            </span>
          ))}
        </div>

        {biggest.map((neo, i) => {
          const diam = parseFloat(neo.estimated_diameter_max_m || 0);
          const barW = Math.min(100, (diam / maxDiam) * 100);
          const isHz = neo.is_potentially_hazardous;

          return (
            <div
              key={neo.neo_id || i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 8,
              }}
            >
              <div
                style={{
                  width: 140,
                  fontSize: 11,
                  color: isHz ? "#ff4466" : "#aabbcc",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {isHz && <span style={{ marginRight: 3 }}>⚠</span>}
                {(neo.name || "Unknown").replace(/[()]/g, "").substring(0, 18)}
              </div>
              <div
                style={{
                  flex: 1,
                  height: 16,
                  background: "rgba(255,255,255,0.03)",
                  borderRadius: 4,
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    width: `${barW}%`,
                    height: "100%",
                    background: isHz
                      ? "linear-gradient(90deg, #ff446688, #ff4466)"
                      : "linear-gradient(90deg, #44ddff44, #44ddff)",
                    borderRadius: 4,
                    minWidth: 2,
                  }}
                />
                {/* Reference lines */}
                {sizeRefs.map((ref) => {
                  const pct = (ref.size / maxDiam) * 100;
                  return pct <= 100 ? (
                    <div
                      key={ref.label}
                      style={{
                        position: "absolute",
                        left: `${pct}%`,
                        top: 0,
                        bottom: 0,
                        width: 1,
                        background: `${ref.color}40`,
                      }}
                    />
                  ) : null;
                })}
              </div>
              <div
                style={{
                  width: 55,
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#c0d0e0",
                  fontFamily: "'JetBrains Mono', monospace",
                  textAlign: "right",
                }}
              >
                {diam >= 1000
                  ? `${(diam / 1000).toFixed(1)}km`
                  : `${diam.toFixed(0)}m`}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── VELOCITY LEADERBOARD ── */}
      <SectionHeader
        icon="💨"
        title={t.fastestAsteroids}
        subtitle={t.fastestSub}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
          gap: 10,
          marginBottom: 28,
        }}
      >
        {[...neos]
          .sort(
            (a, b) =>
              parseFloat(b.relative_velocity_kms || 0) -
              parseFloat(a.relative_velocity_kms || 0),
          )
          .slice(0, 10)
          .map((neo, i) => {
            const vel = parseFloat(neo.relative_velocity_kms || 0);
            const isHz = neo.is_potentially_hazardous;
            return (
              <div
                key={neo.neo_id || i}
                style={{
                  background: "rgba(255,255,255,0.02)",
                  border: `1px solid ${isHz ? "rgba(255,68,102,0.15)" : "rgba(255,255,255,0.05)"}`,
                  borderRadius: 10,
                  padding: "12px 14px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      color: isHz ? "#ff4466" : "#c0d0e0",
                      fontWeight: 500,
                    }}
                  >
                    {isHz && <span style={{ marginRight: 3 }}>⚠</span>}
                    {(neo.name || "Unknown")
                      .replace(/[()]/g, "")
                      .substring(0, 16)}
                  </span>
                  <span style={{ fontSize: 10, color: "#445566" }}>
                    #{i + 1}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#ffaa44",
                    fontFamily: "'JetBrains Mono', monospace",
                    marginTop: 6,
                  }}
                >
                  {vel.toFixed(1)}
                  <span
                    style={{ fontSize: 10, color: "#667788", fontWeight: 400 }}
                  >
                    {" "}
                    km/s
                  </span>
                </div>
                <div style={{ fontSize: 9, color: "#556677", marginTop: 4 }}>
                  ≈ {((vel / 299792) * 100).toFixed(4)}% {t.speedOfLight}
                </div>
              </div>
            );
          })}
      </div>

      {/* ── WAREHOUSE SUMMARY ── */}
      {risk.length > 0 && (
        <>
          <SectionHeader
            icon="🏗️"
            title={t.warehouseSummary}
            subtitle={t.warehouseSummarySub}
          />
          <div
            style={{
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
              marginBottom: 24,
            }}
          >
            {risk.map((r) => (
              <div
                key={String(r.is_potentially_hazardous)}
                style={{
                  background: "rgba(255,255,255,0.02)",
                  border: `1px solid ${r.is_potentially_hazardous ? "rgba(255,68,102,0.2)" : "rgba(0,255,136,0.15)"}`,
                  borderRadius: 12,
                  padding: 20,
                  flex: 1,
                  minWidth: 250,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 16,
                  }}
                >
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: r.is_potentially_hazardous ? "#ff4466" : "#00ff88",
                    }}
                  >
                    {r.is_potentially_hazardous
                      ? `⚠ ${t.hazardous}`
                      : `✓ ${t.nonHazardous}`}
                  </span>
                  <span
                    style={{
                      fontSize: 28,
                      fontWeight: 700,
                      fontFamily: "'JetBrains Mono', monospace",
                      color: r.is_potentially_hazardous ? "#ff4466" : "#00ff88",
                    }}
                  >
                    {r.total_objects}
                  </span>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 12,
                  }}
                >
                  {[
                    {
                      l: t.avgDiameter,
                      v: `${(r.avg_diameter_m || 0).toFixed(0)}m`,
                    },
                    {
                      l: t.maxDiameter,
                      v: `${(r.max_diameter_m || 0).toFixed(0)}m`,
                    },
                    {
                      l: t.avgVelocity,
                      v: `${(r.avg_velocity_kms || 0).toFixed(1)} km/s`,
                    },
                    {
                      l: t.minMissDist,
                      v: `${((r.min_miss_distance_km || 0) / 1e6).toFixed(2)}M km`,
                    },
                  ].map(({ l, v }) => (
                    <div key={l}>
                      <div
                        style={{
                          fontSize: 10,
                          color: "#445566",
                          textTransform: "uppercase",
                          letterSpacing: 1,
                        }}
                      >
                        {l}
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: "#aabbcc",
                          fontFamily: "'JetBrains Mono', monospace",
                        }}
                      >
                        {v}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Data Source */}
      <div
        style={{
          background: "rgba(68,221,255,0.03)",
          border: "1px solid rgba(68,221,255,0.1)",
          borderRadius: 12,
          padding: 16,
          fontSize: 12,
          color: "#778899",
          lineHeight: 1.7,
        }}
      >
        <span style={{ color: "#44ddff", fontWeight: 600 }}>
          ℹ {t.dataSource}
        </span>{" "}
        {t.dataSourceText}
      </div>
    </div>
  );
}
